/* eslint-disable @typescript-eslint/ban-ts-comment */
import omitBy from 'lodash/omitBy';
import type { NextRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import type { StoreApi } from 'zustand';

import {
	parseDateRangeType,
	parseKeyword,
	parseOrder,
	parsePurchasesCompleteStatusType,
	parseSortBy,
} from 'Components/Account/AccountPage.helpers';
import {
	DEFAULT_BASIC_ACCOUNT_FILTERS_STATE,
	DEFAULT_PURCHASES_COMPLETE_FILTERS_STATE,
} from 'Stores/AccountFiltersStore/AccountFiltersStore.consts';
import type { AccountCategory, AccountSlugs } from 'Types/account';
import { Order } from 'Types/generic';
import { isTrue } from 'Utilities/helpers';

import type {
	AccountFiltersStore,
	AccountStoreFilters,
	BasicAccountFiltersStore,
	BasicAccountFilterState,
	PurchasesCompleteFiltersState,
	PurchasesCompleteFiltersStore,
} from './AccountFiltersStore.types';

export const slugStateMutator = (
	state: AccountFiltersStore,
	category: AccountCategory,
	slug: AccountSlugs,
	mutator: Partial<BasicAccountFilterState | PurchasesCompleteFiltersState>,
): AccountFiltersStore => ({
	...state,
	[category]: {
		...state[category],
		[slug]: {
			// @ts-ignore
			...state[category][slug],
			...mutator,
		},
	},
});

export const getBasicAccountFiltersStore = (
	set: StoreApi<AccountFiltersStore>['setState'],
	category: AccountCategory,
	slug: AccountSlugs,
	initialValues?: Partial<BasicAccountFilterState | PurchasesCompleteFiltersState>,
	defaultValues: BasicAccountFilterState = DEFAULT_BASIC_ACCOUNT_FILTERS_STATE,
): BasicAccountFiltersStore => ({
	...defaultValues,
	...initialValues,
	setSlugState: (newState) => set((state) => slugStateMutator(state, category, slug, newState)),
});

export const getPurchasesCompleteAccountFiltersStore = (
	set: StoreApi<AccountFiltersStore>['setState'],
	category: AccountCategory,
	slug: AccountSlugs,
	initialValues?: Partial<BasicAccountFilterState | PurchasesCompleteFiltersState>,
	defaultValues: PurchasesCompleteFiltersState = DEFAULT_PURCHASES_COMPLETE_FILTERS_STATE,
): PurchasesCompleteFiltersStore => ({
	...defaultValues,
	...getBasicAccountFiltersStore(set, category, slug, initialValues, defaultValues),
	setSlugState: (newState) => set((state) => slugStateMutator(state, category, slug, newState)),
});

// Type-guards
export const isPurchasesCompleteFiltersStore = (
	val: PurchasesCompleteFiltersStore | unknown,
): val is PurchasesCompleteFiltersStore =>
	// @ts-ignore
	val?.purchasesCompleteStatusType !== undefined;

// URL helpers
export const parseUrlQueryToAccountFilterSlugState = (
	query: ParsedUrlQuery,
	store: AccountStoreFilters,
): BasicAccountFilterState | PurchasesCompleteFiltersState => {
	const dateRangeType = parseDateRangeType(query, DEFAULT_BASIC_ACCOUNT_FILTERS_STATE.dateRangeType);
	const keyword = parseKeyword(query, DEFAULT_BASIC_ACCOUNT_FILTERS_STATE.keyword);
	const purchasesCompleteStatusType = parsePurchasesCompleteStatusType(
		query,
		DEFAULT_PURCHASES_COMPLETE_FILTERS_STATE.purchasesCompleteStatusType,
	);
	const sortBy = parseSortBy(query);
	const order = parseOrder(query, Order.Descending);
	const hideDelivered = isTrue(String(query.hideDelivered));

	return {
		dateRangeType,
		keyword,
		order,
		sortBy,
		...(isPurchasesCompleteFiltersStore(store) ? { hideDelivered, purchasesCompleteStatusType } : {}),
	};
};

type SanitizedQuery = { [key: string]: string | string[] | undefined | null | number | boolean };
export const sanitizeRouterQuery = (query: SanitizedQuery): SanitizedQuery =>
	omitBy(query, (val) => val === null || val === undefined || val === '');

export const setAccountFilter = (
	// we need to deprecate this as we have useAccountRouter changePage() now
	router: NextRouter,
	state: Partial<BasicAccountFilterState | PurchasesCompleteFiltersState>,
): void => {
	// eslint-disable-next-line @typescript-eslint/no-floating-promises
	router.push(
		{
			pathname: router.pathname,
			query: { ...sanitizeRouterQuery({ ...router.query, ...state }), page: 1 },
		},
		undefined,
		{ shallow: true },
	);
};
